import React, { useState, useEffect } from 'react'

import Button from '@material-ui/core/Button'
import { withRouter } from 'react-router-dom'
import StarRatings from 'react-star-ratings'
import { toast } from 'react-toastify'

import Loader from 'components/atoms/Loader'
import GetDirections from 'components/molecules/GetDirections'
import Reviews from 'components/molecules/Reviews'
import Team from 'components/molecules/Team'
import BookMeeting from 'components/organisms/BookMeeting'
import LoginModal from 'components/organisms/LoginModal'
import useUserInfo from 'hooks/useUserInfo'
import { ReactComponent as ExperienceIcon } from 'images/icon-badge.svg'
import { ReactComponent as CircleCloseIcon } from 'images/icon-cancel-case.svg'
import { ReactComponent as CircleCheckIcon } from 'images/icon-check-circle.svg'
import { ReactComponent as MessageIcon } from 'images/icon-comment.svg'
import { ReactComponent as MembersIcon } from 'images/icon-people.svg'
import { ReactComponent as PhoneIcon } from 'images/icon-phone-call.svg'
import { ReactComponent as LanguageIcon } from 'images/icon-translate.svg'
import useStartChatRoom from 'services/firebase/hooks/chat/useStartChatRoom'
import useGetLawyerByUid from 'services/firebase/hooks/lawyers/useGetLawyerByUid'
import useGetLawyerReviews from 'services/firebase/hooks/lawyers/useGetLawyerReviews'
import useGetUserByUid from 'services/firebase/hooks/users/useGetUserByUid'
import { expertise } from 'utils/constants'

import './ProfilePage.scss'

const ProfilePage = ({ match, history }) => {
  const [userInfo] = useUserInfo()

  const [loading, setLoading] = useState(true)
  const [lawyerData, setLawyerData] = useState(null)
  const [reviews, setReviews] = useState([])
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [actionType, setActionType] = useState(null)
  const [showPhoneNumber, setShowPhoneNumber] = useState(false)

  const doStartChatRoom = useStartChatRoom()
  const doGetLawyerByUid = useGetLawyerByUid()
  const doGetLawyerReviews = useGetLawyerReviews()
  const doGetUserByUid = useGetUserByUid()

  useEffect(() => {
    if (match.params.slug) {
      doGetLawyerByUid(match.params.slug)
        .then(async (result) => {
          if (result) {
            doGetLawyerReviews(result[0].uid).then(async (items) => {
              const clientsReviews = await items.map((item) => {
                return doGetUserByUid(item.uid).then((client) => {
                  return {
                    ...item,
                    author: client[0].displayName || '--',
                    image: client[0].photoURL || null,
                  }
                })
              })

              Promise.all(clientsReviews).then((allReviews) => {
                setReviews(allReviews)
              })
            })
            setLawyerData(result[0])
            setLoading(false)
          }
        })
        .catch((err) => {
          history.push('/')
        })
    } else {
      history.push('/')
    }
  }, [])

  const handleStartChat = (uid) => {
    if (uid) {
      if (match.params.slug === uid) {
        toast.error('You cannot start a chat with yourself.')
      } else {
        doStartChatRoom(match.params.slug)
      }
    } else {
      toast.error('To message please login/register free.')
    }
  }

  const handleActionButtons = (type) => {
    if (userInfo && typeof userInfo.uid !== 'undefined') {
      if (type === 'chat') {
        handleStartChat(userInfo.uid)
      }
      if (type === 'phone') {
        setShowPhoneNumber(true)
      }
    } else {
      setActionType(type)
      setShowLoginModal(true)
    }
  }

  const handleModalRedirect = (userData) => {
    if (actionType === 'chat') {
      handleStartChat(userData)
    }
    if (actionType === 'phone') {
      setShowPhoneNumber(true)
    }
    setShowLoginModal(false)
  }

  const openLinkedIn = () => {
    window.open(lawyerData.linkedIn, '_blank')
  }

  const handleWithAnchor = (hash) => {
    if (hash !== '') {
      setTimeout(() => {
        const id = hash.replace('#', '')
        const element = document.getElementById(id)
        if (element) element.scrollIntoView()
      }, 0)
    }
  }

  return (
    <div className='p-profilePage'>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className='p-profilePage__header'>
            <div className='p-profilePage__headerContent'>
              {/*<button className='p-profilePage__headerContent--bookmark'>*/}
              {/*  <BookmarkIcon />*/}
              {/*</button>*/}
              <div
                className={`p-profilePage__headerContent--image ${
                  lawyerData.onlineNow ? '-online' : ''
                }`}
              >
                <img src={lawyerData.photoURL} alt='' />
              </div>
              <div className='p-profilePage__headerContent--info'>
                <div className='p-profilePage__headerContent--info-title'>
                  <span>
                    {lawyerData.expertise
                      .map(
                        (item) =>
                          expertise.find((exp) => exp.value === item).label
                      )
                      .join(', ')}
                  </span>
                  <h2>
                    {lawyerData.name} {lawyerData.surname}
                  </h2>
                </div>
                <div className='p-profilePage__headerContent--info-reviews'>
                  <StarRatings
                    svgIconPath='M48.856,22.73c0.983-0.958,1.33-2.364,0.906-3.671c-0.425-1.307-1.532-2.24-2.892-2.438l-12.092-1.757
              c-0.515-0.075-0.96-0.398-1.19-0.865L28.182,3.043c-0.607-1.231-1.839-1.996-3.212-1.996c-1.372,0-2.604,0.765-3.211,1.996
              L16.352,14c-0.23,0.467-0.676,0.79-1.191,0.865L3.069,16.622c-1.359,0.197-2.467,1.131-2.892,2.438
              c-0.424,1.307-0.077,2.713,0.906,3.671l8.749,8.528c0.373,0.364,0.544,0.888,0.456,1.4L8.224,44.701
              c-0.183,1.06,0.095,2.091,0.781,2.904c1.066,1.267,2.927,1.653,4.415,0.871l10.814-5.686c0.452-0.237,1.021-0.235,1.472,0
              l10.815,5.686c0.526,0.277,1.087,0.417,1.666,0.417c1.057,0,2.059-0.47,2.748-1.288c0.687-0.813,0.964-1.846,0.781-2.904
              l-2.065-12.042c-0.088-0.513,0.083-1.036,0.456-1.4L48.856,22.73z'
                    starDimension={'25px'}
                    starRatedColor='#FFB800'
                    rating={lawyerData.rating}
                    numberOfStars={5}
                    starSpacing={'3px'}
                  />
                </div>
                <div className='p-profilePage__headerContent--info-organization'>
                  <span>{lawyerData.lawFirmName}</span>
                </div>
                <div className='p-profilePage__headerContent--info-actions'>
                  <div className='p-profilePage__headerContent--info-actions-hourlyRate'>
                    <span>Hourly rate</span>
                    <strong>£{lawyerData.hourPrice}</strong>
                  </div>
                  <div className='p-profilePage__headerContent--info-actions-fixedFee'>
                    <span>Fixed Fee Option</span>
                    {lawyerData.fixedFee ? (
                      <CircleCheckIcon className='positive' />
                    ) : (
                      <CircleCloseIcon className='negative' />
                    )}
                  </div>
                  <div className='p-profilePage__headerContent--info-buttons'>
                    {showPhoneNumber && lawyerData.phone ? (
                      <span className='-phone'>
                        <PhoneIcon /> {lawyerData.phone}
                      </span>
                    ) : (
                      <button
                        className={`-phone ${
                          showPhoneNumber ? '-phoneNumber' : ''
                        }`}
                        onClick={() => handleActionButtons('phone')}
                      >
                        <PhoneIcon />
                        <span>Call Now</span>
                      </button>
                    )}

                    <button
                      className='-message'
                      onClick={() => handleActionButtons('chat')}
                    >
                      <MessageIcon />
                      <span>Message</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='p-profilePage__header--tabs'>
              <button
                onClick={() => handleWithAnchor('about')}
                className='-active'
              >
                About
              </button>
              <button onClick={() => handleWithAnchor('location')}>
                Location
              </button>
              {reviews.length > 0 && (
                <button onClick={() => handleWithAnchor('reviews')}>
                  Reviews
                </button>
              )}
            </div>
          </div>

          <div className='p-profilePage__content'>
            <div className='p-profilePage__highlights'>
              <div className='p-profilePage__highlights--item'>
                <div className='p-profilePage__highlights--item-icon'>
                  <MembersIcon />
                </div>
                <span className='p-profilePage__highlights--item-text'>
                  {lawyerData.numberOfEmployees} Team <br />
                  members
                </span>
              </div>
              <div className='p-profilePage__highlights--item'>
                <div className='p-profilePage__highlights--item-icon'>
                  <ExperienceIcon />
                </div>
                <span className='p-profilePage__highlights--item-text'>
                  {lawyerData.practisingSince} Years <br />
                  Experience
                </span>
              </div>
              <div className='p-profilePage__highlights--item'>
                <div className='p-profilePage__highlights--item-icon'>
                  <CircleCheckIcon />
                </div>
                <span className='p-profilePage__highlights--item-text'>
                  5 Cases <br />
                  Completed
                </span>
              </div>
              <div className='p-profilePage__highlights--item'>
                <div className='p-profilePage__highlights--item-icon'>
                  <LanguageIcon />
                </div>
                <span className='p-profilePage__highlights--item-text'>
                  {lawyerData.languages.map((lang) => lang.name).join(', ')}
                </span>
              </div>
            </div>
            <div id='about' className='p-profilePage__info'>
              <div
                className='p-profilePage__infoBox'
                style={{ justifyContent: 'flex-start' }}
              >
                <h3>Practice Areas</h3>
                <ul>
                  {lawyerData.practiceAreas.map((item) => (
                    <li key={item.value}>
                      <CircleCheckIcon />
                      <span>{item.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className='p-profilePage__infoBox'
                style={{ justifyContent: 'flex-start' }}
              >
                <h3>Bio</h3>
                <p>{lawyerData.about || 'Bio not informed'}</p>
                {lawyerData.linkedIn && (
                  <Button
                    onClick={openLinkedIn}
                    variant='contained'
                    color='primary'
                    fullWidth
                  >
                    See Linkedin
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className='p-profilePage__bookMeeting'>
            <BookMeeting availability={lawyerData.availability} />
          </div>
          <Team uid={match.params.slug} />
          <div id='location' className='p-profilePage__getDirections'>
            <GetDirections lawyerData={lawyerData} />
          </div>
          {reviews.length > 0 && (
            <div id='reviews' className='p-profilePage__reviews'>
              <div className='p-profilePage__reviews--title'>
                <h2>Reviews</h2>
                <div className='p-profilePage__reviews--title-rating'>
                  <div className='p-profilePage__reviews--title-ratingStars'>
                    <StarRatings
                      svgIconPath='M48.856,22.73c0.983-0.958,1.33-2.364,0.906-3.671c-0.425-1.307-1.532-2.24-2.892-2.438l-12.092-1.757
              c-0.515-0.075-0.96-0.398-1.19-0.865L28.182,3.043c-0.607-1.231-1.839-1.996-3.212-1.996c-1.372,0-2.604,0.765-3.211,1.996
              L16.352,14c-0.23,0.467-0.676,0.79-1.191,0.865L3.069,16.622c-1.359,0.197-2.467,1.131-2.892,2.438
              c-0.424,1.307-0.077,2.713,0.906,3.671l8.749,8.528c0.373,0.364,0.544,0.888,0.456,1.4L8.224,44.701
              c-0.183,1.06,0.095,2.091,0.781,2.904c1.066,1.267,2.927,1.653,4.415,0.871l10.814-5.686c0.452-0.237,1.021-0.235,1.472,0
              l10.815,5.686c0.526,0.277,1.087,0.417,1.666,0.417c1.057,0,2.059-0.47,2.748-1.288c0.687-0.813,0.964-1.846,0.781-2.904
              l-2.065-12.042c-0.088-0.513,0.083-1.036,0.456-1.4L48.856,22.73z'
                      starDimension={'18px'}
                      starRatedColor='#FFB800'
                      rating={lawyerData.rating}
                      numberOfStars={5}
                      starSpacing={'3px'}
                    />
                  </div>
                  <b>{lawyerData.rating.toFixed(1)}</b>
                  <small>
                    ({reviews.length}{' '}
                    {reviews.length === 1 ? 'Review' : 'Reviews'})
                  </small>
                </div>
              </div>
              <Reviews reviews={reviews} />
            </div>
          )}
        </>
      )}

      {showLoginModal && (
        <LoginModal
          handleModalRedirect={handleModalRedirect}
          handleCloseModal={setShowLoginModal}
        />
      )}
    </div>
  )
}

export default withRouter(ProfilePage)
